import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { OnboardingCreateStoryConstants as ui } from '../../Config/UIConstants';
import { CommonTabbedContainer, Tab } from '../../SharedComponents/CommonTabbedContainer';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullScreenModalContainer } from '../../SharedComponents/FullScreenModalContainer';
import { ModalActionButtonBar } from '../../SharedComponents/ModalActionButtonBar';
import { RemovalConfirmationPopup } from '../../SharedComponents/RemovalConfirmationPopup';
import { StoryTitleField } from '../../SharedComponents/StoryTitleField';
import { Routes } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { CheckMarkCircleIcon, PadLock } from '../../images/images';
import { colors } from '../../styles/colors';
import { largeShadow } from '../../styles/styleObjects';

interface IProps {
    store: ApplicationStore;
}

interface IState {
    activeTab: Tab;
    selectedFamilyIndex: number;
    newStoryName: string;
    showExitConfirm: boolean;
    storyWasCreated: boolean;
    warnShareStory: boolean;
    storyTitlePlaceholder: string;
    storyTitleDefault: string;
}

@inject('store')
@observer
export class AddStoryPage extends React.Component<IProps & RouteComponentProps<any>, IState> {
    public state: IState = {
        activeTab: Tab.LEFT,
        selectedFamilyIndex: 0,
        newStoryName: '',
        showExitConfirm: false,
        storyWasCreated: false,
        warnShareStory: false,
        storyTitlePlaceholder: '',
        storyTitleDefault: '',
    };

    private createStory(): void {
        let { newStoryName, storyTitleDefault } = this.state;

        this.setState({ warnShareStory: false });

        if (newStoryName.trim() === '') {
            newStoryName = storyTitleDefault;
        }

        let familyID: number | undefined = undefined;
        if (this.state.activeTab === Tab.RIGHT) {
            const families = this.props.store.userFamilyStore.userFamilies;
            familyID = families[this.state.selectedFamilyIndex].id;
        }

        this.setState({ storyWasCreated: true });

        this.props.store.storyStore
            .createStory(newStoryName, familyID)
            .then(story => {
                let photoIDsToAdd = this.getPhotoIDsToAdd();

                if (photoIDsToAdd) {
                    this.props.store.storyStore.addPhotosToStory(story.id, photoIDsToAdd).catch(error => console.log('ERROR: ', error));
                }
            })
            .catch(error => {
                console.log('ERROR: Creating story failed: ', error);
            });
    }

    private onClickCreateStory(): void {
        if (this.state.activeTab === Tab.RIGHT) {
            this.setState({ warnShareStory: true });
        } else {
            this.createStory();
        }
    }

    private getPhotoIDsToAdd(): Array<number> | undefined {
        let photosToAdd = undefined;
        if (this.props.location.state && this.props.location.state.photoIDsForStory) {
            photosToAdd = this.props.location.state.photoIDsForStory;
        }

        return photosToAdd;
    }

    public render() {
        if (this.props.store.userFamilyStore === undefined || this.props.store.userFamilyStore.familiesAreLoaded === false) {
            return null;
        }

        const families = this.props.store.userFamilyStore.userFamilies;

        const photosToAdd = this.getPhotoIDsToAdd();

        return (
            <FullScreenModalContainer
                {...this.props}
                justifyContent="space-between"
                alignItems="center"
                onPressExit={() => (this.state.newStoryName === '' ? this.props.history.push(Routes.ALBUMS) : this.setState({ showExitConfirm: true }))}
            >
                <p style={styles.headerText}>{photosToAdd ? `You've added ${photosToAdd.length} photos, let's create the story album` : ui.HEADER}</p>
                <div style={styles.contentContainer}>
                    <p style={styles.topSubHeader}>
                        A good title helps identify an album’s subject matter (e.g., “1972 Family Reunion” or “2006 Roger and June’s Wedding”). Add your own title now or accept the default. It’s easy to change the title later.
                    </p>

                    <StoryTitleField
                        updateValue={title => {
                            this.setState({ newStoryName: title });
                        }}
                        value={this.state.newStoryName}
                    />

                    {/* This was removed per Eric's request. To show again, remove "false" from the conditional*/}
                    {photosToAdd && false && (
                        <div>
                            <p style={styles.midSubHeader}>{ui.SUB_HEADER}</p>
                            <p style={styles.bottomSubHeader}>{ui.SECOND_INSTRUCTION}</p>
                            {this.state.activeTab === Tab.RIGHT && <p style={styles.shareWarning}>{ui.SHARE_WARNING}</p>}
                            <CommonTabbedContainer
                                leftHeader={ui.KEEP_PRIVATE_HEADER}
                                rightHeader={ui.SHARE_HEADER}
                                activeTab={this.state.activeTab}
                                onPressTab={(tab: Tab) => this.setState({ activeTab: tab })}
                                leftComponent={() => (
                                    <div style={styles.leftTabContainer}>
                                        <img src={PadLock} style={styles.lockImage} />
                                        <p style={styles.lockText}>{ui.NO_SHARE}</p>
                                    </div>
                                )}
                                rightComponent={() => (
                                    <div style={styles.rightTabContainer}>
                                        {families.map((family, index) => {
                                            return (
                                                <div onClick={() => this.setState({ selectedFamilyIndex: index })} key={index} style={styles.familyRowContainer}>
                                                    <p>{family.name}</p>
                                                    <div>
                                                        {this.state.selectedFamilyIndex === index ? (
                                                            <img src={CheckMarkCircleIcon} style={styles.familyCheckmark} />
                                                        ) : (
                                                            <div style={styles.emptyCheckMark} />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div style={styles.familyRowContainer} onClick={() => this.props.history.push(Routes.ADD_FAMILY)}>
                                            <p style={styles.createFamilyText}>{ui.CREATE_NEW_FAMILY}</p>
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    )}
                </div>
                <ModalActionButtonBar
                    cancelOnClick={() => this.props.history.push(Routes.ALBUMS)}
                    confirmOnClick={() => this.onClickCreateStory()}
                    cancelText={ui.CANCEL}
                    confirmContent={ui.CREATE_ALBUM}
                    altPadding={25}
                />
                {this.state.showExitConfirm && (
                    <RemovalConfirmationPopup
                        headerText={ui.EXIT_HEADER}
                        subHeaderText={ui.EXIT_SUB_HEADER}
                        cancelText={ui.EXIT_CANCEL}
                        removeText={ui.EXIT_CONFIRM}
                        onClickCancel={() => this.setState({ showExitConfirm: false })}
                        onClickRemove={() => this.props.history.push(Routes.ALBUMS)}
                    />
                )}
                {this.state.storyWasCreated && (
                    <ExitableAlert headerText={ui.STORY_CREATED(this.state.newStoryName)} onConfirmClick={() => this.props.history.push(Routes.ALBUMS)} confirmText={ui.OK} />
                )}
                {this.state.warnShareStory && (
                    <RemovalConfirmationPopup
                        headerText={ui.SHARE_WARNING}
                        subHeaderText={ui.CANNOT_BE_UNDONE}
                        removeText={ui.GO_BACK}
                        cancelText={ui.CONTINUE}
                        onClickCancel={() => this.createStory()}
                        onClickRemove={() => this.setState({ warnShareStory: false })}
                    />
                )}
            </FullScreenModalContainer>
        );
    }
}

const styles = {
    createFamilyText: { color: colors.ACTION_GREEN },
    familyRowContainer: {
        height: 50,
        cursor: 'pointer',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        display: 'flex',
        paddingLeft: 15,
        paddingRight: 15,
        borderBottom: '1px solid rgb(245, 245, 245)'
    } as React.CSSProperties,
    familyCheckmark: {
        height: 20,
        width: 20,
        borderRadius: 20
    },
    rightTabText: {
        color: 'black',
        paddingTop: 3,
        marginLeft: 8,
        textAlign: 'center',
        width: '100%'
    } as React.CSSProperties,
    rightTabContainer: {
        width: '100%',
        backgroundColor: 'white',
        alignItems: 'center',
        flexDirection: 'column',
        maxHeight: 200,
        overflowY: 'scroll'
    } as React.CSSProperties,
    lockText: {
        color: 'black',
        paddingTop: 3,
        marginLeft: 8
    },
    lockImage: {
        marginLeft: 15,
        height: 15,
        width: 15,
        objectFit: 'contain'
    } as React.CSSProperties,
    leftTabContainer: {
        width: '100%',
        backgroundColor: 'white',
        height: 50,
        display: 'flex',
        alignItems: 'center'
    } as React.CSSProperties,
    bottomSubHeader: {
        width: '100%',
        textAlign: 'center',
        fontSize: 18,
        opacity: 0.8,
        marginTop: 10,
        marginBottom: 20
    } as React.CSSProperties,
    shareWarning: {
        width: '100%',
        textAlign: 'center',
        fontSize: 18,
        opacity: 0.8,
        marginTop: -10,
        marginBottom: 10
    } as React.CSSProperties,
    midSubHeader: {
        fontSize: 22,
        marginTop: 30,
        color: 'black',
        fontWeight: 900,
        textAlign: 'center'
    } as React.CSSProperties,
    topSubHeader: {
        width: '100%',
        textAlign: 'center',
        fontSize: 18,
        opacity: 0.8,
        marginTop: 50
    } as React.CSSProperties,
    inputContainer: {
        width: '100%',
        height: 85,
        backgroundColor: 'white',
        boxShadow: largeShadow,
    },
    headerText: {
        fontSize: 25,
        color: colors.DARK_TEXT,
        marginTop: 50
    },
    contentContainer: {
        display: 'flex',
        width: 450,
        height: '50%',
        flexDirection: 'column',
        flex: 1
    } as React.CSSProperties,
    storyNameInputWrap: {
        position: 'relative',
        marginTop: 20,
        marginBottom: 15,
    } as React.CSSProperties,
    storyNameInfoIcon: {
        height: '2em',
        position: 'absolute',
        top: 0,
        right: '-2.5em'
    } as React.CSSProperties,
    bottomContainer: {
        height: 150,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column'
    } as React.CSSProperties,
    buttonText: {
        fontSize: 14,
        fontWeight: 200,
        letterSpacing: 1
    } as React.CSSProperties,
    buttonContainer: {
        height: 48,
        width: 180
    },
    activityIndicator: {
        width: 80,
        height: 15,
        marginTop: 35
    },
    emptyCheckMark: {
        height: 20,
        width: 20,
        borderRadius: 20,
        border: '1px solid #00000022'
    }
};



// WEBPACK FOOTER //
// ./src/Pages/StoriesPage/AddStoryPage.tsx